import { Add, Cancel, ViewWeekOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  IconButton,
  Input,
  Menu,
  MenuButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import Table from "@mui/joy/Table";
import { Row, flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useThrottledCallback } from "use-debounce";
import { useShallow } from "zustand/react/shallow";

import { useIsKeyDownRef } from "@/hooks/useIsKeyDown.ts";

import { Route } from "../../core/types";
import { useModal } from "../modals/useModal.tsx";
import { useRoutesUiContext } from "../RoutesProvider/RoutesProvider.tsx";
import { useRoutesUiStore } from "../RoutesProvider/useRoutesUiStore.ts";
import ActiveFiltersIndicator from "./ActiveFiltersIndicator";
import { TableContextMenuContent } from "./ContextMenu.tsx";
import { useRoutesTableStore } from "./RoutesProvider2.tsx";
import TableHeader from "./TableHeader.tsx";

const SearchBox = () => {
  const { filterParams, setFilterParam, removeFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
      removeFilterParam: state.removeFilterParam,
    })),
  );
  const ref = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (filterParams.searchQuery && ref.current) {
      ref.current.value = filterParams.searchQuery;
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const search = useThrottledCallback((q: string) => setFilterParam("searchQuery", q), 200);

  return (
    <Input
      slotProps={{
        input: { ref: ref },
      }}
      size="sm"
      sx={{ width: 200 }}
      placeholder="Search..."
      onChange={(e) => {
        console.log(e.target.value);
        search(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && ref.current) {
          ref.current.value = "";
          removeFilterParam("searchQuery");
        }
      }}
      endDecorator={
        filterParams.searchQuery ? (
          <IconButton
            onClick={() => {
              if (ref.current) {
                ref.current.value = "";
                removeFilterParam("searchQuery");
              }
            }}
            sx={{ width: "18px", height: "18px" }}
          >
            <Cancel sx={{ width: "18px", height: "18px", color: "neutral.600" }} />
          </IconButton>
        ) : null
      }
    />
  );
};

const TableControls = () => {
  const { displayFilterSidebar, setDisplayFilterSidebar } = useRoutesUiStore(
    useShallow((state) => ({
      displayFilterSidebar: state.displayFilterSidebar,
      setDisplayFilterSidebar: state.setDisplayFilterSidebar,
    })),
  );
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        padding: 0.75,
        paddingX: 1,
      }}
    >
      <SearchBox />
      {/* <ViewModeIndicator /> */}
      <Box sx={{ flex: 1 }}>
        <ActiveFiltersIndicator />
      </Box>
      <Button
        variant="solid"
        color="secondary"
        size="sm"
        onClick={() => setDisplayFilterSidebar(!displayFilterSidebar)}
      >
        Filter...
      </Button>
      <ColumnSelector />
    </Box>
  );
};

const ColumnSelector = () => {
  const { table } = useRoutesUiContext();
  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{ root: { size: "sm" } }}
        color="secondary"
      >
        <ViewWeekOutlined />
      </MenuButton>
      <Menu placement="bottom-end">
        {table.getAllColumns().map((column) => {
          if (column.id === "selected") {
            return null;
          }
          return (
            <Checkbox
              key={column.id}
              checked={column.getIsVisible()}
              onChange={column.getToggleVisibilityHandler()}
              disabled={!column.getCanHide()}
              label={
                typeof column.columnDef.header === "string" ? column.columnDef.header : column.id
              }
              size="sm"
              sx={{
                paddingX: 1,
                paddingY: 0.5,
                display: "flex",
              }}
            />
          );
        })}
      </Menu>
    </Dropdown>
  );
};

const Footer = () => {
  const { table } = useRoutesUiContext();

  const selectedRouteUuidsCount = table.getSelectedRowModel().rows.length;
  const allRoutesCount = table.getRowModel().rows.length;

  if (selectedRouteUuidsCount === 0) {
    return null;
  }
  return (
    <Box className="footer">
      <Box
        sx={{
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: 0.5,
          paddingX: 1,
          borderTop: "1px solid",
          borderColor: "neutral.200",
          backgroundColor: "neutral.100",
          justifyContent: "center",
          paddingBottom: "calc(4px + env(safe-area-inset-bottom)) !important",
        }}
      >
        <Typography fontSize="sm" fontWeight="bold">
          {selectedRouteUuidsCount} of {allRoutesCount} routes selected
        </Typography>
        <Dropdown>
          <MenuButton variant="solid" color="primary" size="sm">
            Actions...
          </MenuButton>
          <Menu size="sm">
            <TableContextMenuContent />
          </Menu>
        </Dropdown>
      </Box>
    </Box>
  );
};

export default function RoutesTable() {
  // TODO: This causes big re-renders. Need to figure out how to make it more atomic.
  // E.g. https://github.com/pmndrs/zustand/tree/main?tab=readme-ov-file#selecting-multiple-state-slices

  const {
    selectedRouteUuids,
    setSelectedRouteUuids,
    highlightedRouteUuids,
    addToSelection,
    removeFromSelection,
  } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      setSelectedRouteUuids: state.setSelectedRouteUuids,
      highlightedRouteUuids: state.highlightedRouteUuids,
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
    })),
  );
  const { table, onAddRoute, canChangeSelection, routesPending } = useRoutesUiContext();

  const setContextMenu = useRoutesUiStore((s) => s.setContextMenu);

  const lastSelectedRouteUuid = useMemo(
    () => Array.from(selectedRouteUuids).pop() ?? null,
    [selectedRouteUuids],
  );

  const { display: displayModal } = useModal();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef(table);
  tableRef.current = table;

  const selectionChangedWithinTableRef = useRef(false);

  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length + 1,
    estimateSize: (index) => (index === rows.length ? 40 : 34), //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    getItemKey: useCallback((index: number) => rows[index]?.id ?? index, [rows]),
    overscan: 50,
    scrollPaddingStart: 40,
    scrollPaddingEnd: 40,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    // measureElement:
    //   typeof window !== "undefined" &&
    //   navigator.userAgent.indexOf("Firefox") === -1
    //     ? (element) => element?.getBoundingClientRect().height
    //     : undefined,
  });

  // Scroll to the first highlighted row, unless the highlight contains a selected
  // row. Sport line highlights are derived from the selection a few commits after
  // it changes, and the selection effect below already handles scrolling for those.
  useLayoutEffect(() => {
    if (!highlightedRouteUuids.size) {
      return;
    }
    const rows = table.getRowModel().rows;
    const highlightFollowsSelection = rows.some(
      (row) => row.getIsSelected() && highlightedRouteUuids.has(row.id),
    );
    if (highlightFollowsSelection) {
      return;
    }
    const firstHighlightedRowIndex = rows.findIndex((row) => highlightedRouteUuids.has(row.id));
    if (firstHighlightedRowIndex !== -1) {
      rowVirtualizer.scrollToIndex(firstHighlightedRowIndex, {
        behavior: "smooth",
        align: "center",
      });
    }
  }, [highlightedRouteUuids, rowVirtualizer, table]);

  // When the selection changes, but NOT from the react-table component - only from external state updates -
  // scroll to the first selected row
  useLayoutEffect(() => {
    if (!selectionChangedWithinTableRef.current && selectedRouteUuids.size) {
      const firstSelectedRowIndex = tableRef.current
        .getRowModel()
        .rows.findIndex((row) => row.getIsSelected());
      if (firstSelectedRowIndex !== -1) {
        rowVirtualizer.scrollToIndex(firstSelectedRowIndex, {
          behavior: "smooth",
          align: "center",
        });
      }
    }
    selectionChangedWithinTableRef.current = false;
  }, [rowVirtualizer, selectedRouteUuids]);

  const onContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        routeUuid: e.currentTarget.dataset.uuid as string,
        type: "route",
      });
    },
    [setContextMenu],
  );

  const selectRowsBetween = useCallback(
    (startUuid: string | null, endUuid: string) => {
      const rows = table.getSortedRowModel().rows;
      const startIndex = startUuid ? rows.findIndex((x) => x.id === startUuid) : 0;
      const endIndex = rows.findIndex((x) => x.id === endUuid);
      if (startIndex === -1 || endIndex === -1) {
        return;
      }
      console.log({ startIndex, endIndex, startUuid, endUuid });

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      console.log({ minIndex, maxIndex });
      const selectedRowUuids = rows.slice(minIndex, maxIndex + 1).map((x) => x.id);
      console.log({ selectedRowUuids });
      addToSelection(selectedRowUuids);
    },
    [addToSelection, table],
  );

  const isShiftKeyPressedRef = useIsKeyDownRef("Shift");
  const isCtrlKeyPressedRef = useIsKeyDownRef(
    ["MacIntel", "iPod", "iPhone", "iPad"].includes(navigator.platform) ? "Meta" : "Control",
  );

  const changeSelection = useCallback(
    (uuid: string, isShiftPressed: boolean, replaceSelectionIfNotSelected: boolean) => {
      selectionChangedWithinTableRef.current = true;
      if (!canChangeSelection) {
        displayModal("UNSAVED_ROUTE_CHANGES_PROMPT");
        return;
      }
      const isSelectedAlready = selectedRouteUuids.has(uuid);
      if (!isShiftPressed) {
        if (replaceSelectionIfNotSelected) {
          if (!isSelectedAlready) {
            setSelectedRouteUuids([uuid]);
          }
        } else {
          if (!isSelectedAlready) {
            addToSelection([uuid]);
          } else {
            removeFromSelection([uuid]);
          }
        }
      } else {
        selectRowsBetween(lastSelectedRouteUuid, uuid);
      }
    },
    [
      addToSelection,
      canChangeSelection,
      displayModal,
      lastSelectedRouteUuid,
      removeFromSelection,
      selectRowsBetween,
      selectedRouteUuids,
      setSelectedRouteUuids,
    ],
  );

  const handleRowClick = useCallback(
    (uuid: string) => {
      if (isCtrlKeyPressedRef.current) {
        changeSelection(uuid, isShiftKeyPressedRef.current, false);
      } else {
        changeSelection(uuid, isShiftKeyPressedRef.current, true);
      }
    },
    [changeSelection, isCtrlKeyPressedRef, isShiftKeyPressedRef],
  );

  const handleRowCheckboxChange = useCallback(
    (uuid: string) => {
      changeSelection(uuid, isShiftKeyPressedRef.current, false);
    },
    [changeSelection, isShiftKeyPressedRef],
  );

  return (
    <Stack sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 1,
          overflow: "hidden",
          minHeight: 0,
          "@media (pointer:coarse)": {
            userSelect: "none",
          },
        }}
      >
        <TableControls />
        <Box
          ref={tableContainerRef}
          sx={{
            overflow: "auto", //our scrollable table container
            position: "relative", //needed for sticky header
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
          <Table
            borderAxis="bothBetween"
            hoverRow
            size="sm"
            sx={{
              display: "grid",
              flex: 1,
              "--TableCell-selectedBackground": (theme) => theme.vars.palette.primary.softBg,
              "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
              ".add-new-row, .add-new-row:hover": {
                backgroundColor: "neutral.100",
              },
              th: {
                backgroundColor: "neutral.100",
              },
              "td, th": {
                paddingX: 1,
                paddingY: 2,
              },
            }}
          >
            <thead
              style={{
                display: "grid",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} style={{ display: "flex", width: "100%" }}>
                  {headerGroup.headers.map((header) => {
                    return <TableHeader key={header.id} header={header} table={table} />;
                  })}
                </tr>
              ))}
            </thead>
            {routesPending ? (
              <tbody
                style={{
                  display: "grid",
                }}
              >
                {Array(20)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={i} style={{ display: "flex" }}>
                      {table.getVisibleFlatColumns().map((column) => (
                        <td
                          key={column.id}
                          style={{
                            width: column.getSize(),
                            height: 34,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Skeleton
                            loading
                            variant="rectangular"
                            width="100%"
                            height="22px"
                            sx={{ display: "block" }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            ) : (
              <tbody
                style={{
                  display: "grid",
                  height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
                  position: "relative", //needed for absolute positioning of rows
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  if (virtualRow.index === rows.length) {
                    return (
                      <tr
                        key="add-new"
                        className="add-new-row"
                        style={{
                          display: "flex",
                          position: "absolute",
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                          width: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="solid"
                          color="secondary"
                          size="sm"
                          sx={{
                            margin: 1,
                          }}
                          startDecorator={<Add />}
                          onClick={() => onAddRoute()}
                        >
                          Add new route
                        </Button>
                      </tr>
                    );
                  }
                  const row = rows[virtualRow.index] as Row<Route>;
                  return (
                    <tr
                      role="checkbox"
                      onClick={() => handleRowClick(row.id)}
                      style={{
                        ...(highlightedRouteUuids.has(row.id)
                          ? ({
                              background: "var(--TableRow-hoverBackground)",
                            } as React.CSSProperties)
                          : {}),
                        ...(row.getIsSelected()
                          ? ({
                              "--TableCell-dataBackground": "var(--TableCell-selectedBackground)",
                              "--TableCell-headBackground": "var(--TableCell-selectedBackground)",
                            } as React.CSSProperties)
                          : {}),
                        display: "flex",
                        position: "absolute",
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                        width: "100%",
                      }}
                      data-index={virtualRow.index} //needed for dynamic row height measurement
                      data-uuid={row.id}
                      // ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
                      key={row.id}
                      onContextMenu={onContextMenu}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            style={{
                              display: "flex",
                              width: cell.column.getSize(),
                              alignItems: "center",
                              height: `${virtualRow.size}px`,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, {
                              ...cell.getContext(),
                              ...(cell.column.id === "selected"
                                ? {
                                    onCheckboxChange: () => handleRowCheckboxChange(row.id),
                                  }
                                : {}),
                            })}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </Table>
        </Box>
      </Box>
      <Footer />
    </Stack>
  );
}
